import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { useToastMessage } from "../contexts/ToastMessageContext.jsx";
import { db, appId } from "../firebase/firebaseConfig.jsx";
import { collection, query, orderBy, onSnapshot, doc, updateDoc } from "firebase/firestore";
import { FiDollarSign as DollarSign, FiClock as Clock, FiCheckCircle as CheckCircle, FiXCircle as XCircle, FiEye as Eye, FiUser as User, FiCalendar as Calendar } from "react-icons/fi";
// import logger from "../utils/logger.jsx"; // 파일이 삭제됨
const logger = console;

const LoanAdminPage = () => {
  const { showToastMessage } = useToastMessage();
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");

  // Load loans
  useEffect(() => {
    const loansRef = collection(db, `artifacts/${appId}/loans`);
    const q = query(loansRef, orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const loansData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setLoans(loansData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const approveLoan = useCallback(async (loanId) => {
    try {
      await updateDoc(doc(db, `artifacts/${appId}/loans`, loanId), {
        status: 'APPROVED',
        approvedAt: new Date(),
        updatedAt: new Date()
      });
    } catch (error) {
      logger.error("Error approving loan:", error);
      throw error;
    }
  }, []);

  const rejectLoan = useCallback(async (loanId, reason) => {
    try {
      await updateDoc(doc(db, `artifacts/${appId}/loans`, loanId), {
        status: 'REJECTED',
        rejectionReason: reason,
        rejectedAt: new Date(),
        updatedAt: new Date()
      });
    } catch (error) {
      logger.error("Error rejecting loan:", error);
      throw error;
    }
  }, []);

  const handleApprove = async (loan) => {
    setLoading(true);
    try {
      await approveLoan(loan.id);
      showToastMessage('Loan approved successfully', 'success');
    } catch (error) {
      showToastMessage('Failed to approve loan', 'error');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (loan) => {
    const reason = promp// t('Enter rejection reason:') // Admin은 다국어 지원 불필요;
    if (!reason || !reason.trim()) {
      showToastMessage('Please enter a reason.', 'warning');
      return;
    }
    setLoading(true);
    try {
      await rejectLoan(loan.id, reason);
      showToastMessage('Loan rejected successfully', 'success');
    } catch (error) {
      showToastMessage('Failed to reject loan', 'error');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const filteredLoans = loans.filter(loan => {
    if (filterStatus === "all") return true;
    return loan.status === filterStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Loan Management</h1>
          <p className="text-gray-600">Review and manage loan applications</p>
        </div>
        <div className="flex space-x-3">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="all">All Loans</option>
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
          </select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Loans</p>
              <p className="text-2xl font-bold text-gray-900">{loans.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-gray-900">
                {loans.filter(loan => loan.status === 'PENDING').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Approved</p>
              <p className="text-2xl font-bold text-gray-900">
                {loans.filter(loan => loan.status === 'APPROVED').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Rejected</p>
              <p className="text-2xl font-bold text-gray-900">
                {loans.filter(loan => loan.status === 'REJECTED').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Loans Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Borrower
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Loan Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredLoans.map((loan) => (
                <motion.tr
                  key={loan.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="hover:bg-gray-50"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                          <User className="h-6 w-6 text-indigo-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {loan.borrowerInfo?.name || 'Unknown'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {loan.borrowerInfo?.email || 'No email'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {(loan.loanAmount || 0).toLocaleString()} KRW
                    </div>
                    <div className="text-sm text-gray-500">
                      {loan.loanTermMonths || 0} months
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      loan.status === 'APPROVED' 
                        ? 'bg-green-100 text-green-800'
                        : loan.status === 'REJECTED'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {loan.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {loan.createdAt?.toDate ? 
                      loan.createdAt.toDate().toLocaleDateString() : 
                      'Unknown'
                    }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {loan.status === 'PENDING' && (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleApprove(loan)}
                          className="text-green-600 hover:text-green-900 flex items-center gap-1"
                          title="Approve loan"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Approve
                        </button>
                        <button
                          onClick={() => handleReject(loan)}
                          className="text-red-600 hover:text-red-900 flex items-center gap-1"
                          title="Reject loan"
                        >
                          <XCircle className="w-4 h-4" />
                          Reject
                        </button>
                      </div>
                    )}
                    {loan.status !== 'PENDING' && (
                      <span className="text-gray-400">No actions</span>
                    )}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredLoans.length === 0 && (
          <div className="text-center py-8">
            <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No loans found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoanAdminPage;
